import * as R from 'ramda'
import humps from 'humps'
import { images } from 'packages/simplesat-config'
import produce from 'immer'

import constants from 'config/constants'

import {
  questionTypeJsonData,
  initialCSATPrimaryQuestion,
  initialNPSPrimaryQuestion,
  initialCESPrimaryQuestion,
  initialFiveStarPrimaryQuestion,
  initialScreenerQuestion,
  questionTemplateType,
  TEMP_ID_PREFIX,
  TEMP_ID_PRIMARY_PREFIX,
} from './constants'

/**
 * Resolve the metric question independent of list position.
 *
 * Prefers the `is_primary_metric` flag from the BE render payload (PET-1965
 * runtime contract). Falls back to `order === 1` for legacy responses that
 * pre-date the backfill, and finally to `questions[0]` as a last resort.
 *
 * @param {Array<object>} questions
 * @returns {object|null}
 */
export const findMetricQuestion = (questions) => {
  if (!Array.isArray(questions) || questions.length === 0) {
    return null
  }
  // Accept both raw BE snake_case (`is_primary_metric`) and camelized form
  // (`isPrimaryMetric`) so this helper works before and after
  // `transformResponseToPresentationalState`.
  const flagged = questions.find(
    (q) => q?.is_primary_metric === true || q?.isPrimaryMetric === true
  )
  if (flagged) {
    return flagged
  }
  // Builder-shape questions (create flow / unsaved edits) carry the survey
  // metric on the question itself and have neither the BE flag nor an `order`
  // — non-metric questions are always metric CUSTOM (see the templates in
  // ./constants.js and questionTypeJsonData).
  const byMetricField = questions.find(
    (q) => q?.metric && q.metric !== constants.questionMetricType.custom
  )
  if (byMetricField) {
    return byMetricField
  }
  const byOrder = questions.find((q) => q?.order === 1)
  return byOrder ?? questions[0]
}

/**
 * Check whether a single question is the primary metric question.
 *
 * Same flag-first precedence as `findMetricQuestion` — prefer the
 * `is_primary_metric` (or camelized `isPrimaryMetric`) runtime contract flag,
 * fall back to `order === 1` for legacy render payloads that pre-date the
 * PET-1965 backfill. Callers use this when they hold a single question
 * reference (e.g. `selectedQuestion` from the survey dashboard context) and
 * need a positional-metric check that survives screener-first reordering.
 *
 * @param {object|null|undefined} question
 * @returns {boolean}
 */
export const isMetricQuestion = (question) => {
  if (!question) {
    return false
  }
  // Precedence MUST mirror findMetricQuestion so the two never disagree on the
  // same question — SurveySettingForm drives `metricQuestion` off
  // findMetricQuestion but the per-card `primary` prop off isMetricQuestion, so
  // a mismatch leaves the metric card (and PrimaryQuestionInput) unrendered.
  //   1. an explicit `is_primary_metric === true` flag is authoritative;
  //   2. otherwise a non-CUSTOM `metric` field identifies the metric question.
  //      Screeners/follow-ups are always CUSTOM, so this also correctly rejects
  //      a screener sitting at order 1 with a present-but-false flag, and it
  //      resolves the metric in an all-flags-present-but-false payload (backfill
  //      lag) where step 1 would otherwise find nothing;
  //   3. once the flag field is present at all, a false value is definitive —
  //      do NOT fall through to the order fallback (screener holds order 1);
  //   4. finally fall back to `order === 1` for legacy payloads that carry
  //      neither the flag nor a metric field.
  if (question.is_primary_metric === true || question.isPrimaryMetric === true) {
    return true
  }
  if (question.metric && question.metric !== constants.questionMetricType.custom) {
    return true
  }
  if ('is_primary_metric' in question || 'isPrimaryMetric' in question) {
    return false
  }
  return question.order === 1
}

export const hasSurveyBeenSaved = (questions) =>
  R.any((question) => !!question.id && !`${question.id}`.startsWith(TEMP_ID_PREFIX), questions)

export const hasActiveVisibilityRule = (condition) =>
  Array.isArray(condition?.rule) && condition.rule.length > 0

/**
 * Walk visibility/gate conditions and detect any source question that sits
 * at or after its target (an unreachable gate — the survey renderer would
 * never fire the rule).
 *
 * @param {Array<object>} questions
 * @returns {Array<{sourceId, targetId, sourceOrder, targetOrder}>}
 */
export const findGateOrderViolations = (questions) => {
  if (!Array.isArray(questions) || questions.length === 0) {
    return []
  }
  const violations = []
  const byId = new Map()
  const byUid = new Map()
  // Effective order is always the live array position (idx + 1). The persisted
  // `order` field goes stale in Formik state — FieldArray `move` reorders the
  // array without renumbering `order` (generateQuestionRequest derives order
  // from index only at save time), so trusting `order` here would keep flagging
  // a violation the user has already fixed by reordering.
  questions.forEach((q, idx) => {
    const effectiveOrder = idx + 1
    if (q?.id != null) {
      byId.set(String(q.id), { q, order: effectiveOrder })
    }
    if (q?.uniqueIdentifier != null) {
      byUid.set(String(q.uniqueIdentifier), { q, order: effectiveOrder })
    }
  })

  // For each question that hosts a visibility condition ("gated"), check that
  // every question the gate reads from ("source") comes strictly before it.
  // Violation shape uses `sourceOrder` = order of the source (data-provider),
  // `targetOrder` = order of the gated question. Violation when
  // sourceOrder >= targetOrder — the source can't be answered before the
  // renderer needs to evaluate the gate.
  questions.forEach((gatedQ, idx) => {
    const targetOrder = idx + 1
    const conditions = Array.isArray(gatedQ?.visibilityCondition) ? gatedQ.visibilityCondition : []
    conditions.forEach((cond) => {
      if (!hasActiveVisibilityRule(cond)) {
        return
      }
      const srcRef = cond?.sourceQuestionId
      if (srcRef == null) {
        return
      }
      const key = String(srcRef)
      const srcEntry = byId.get(key) || byUid.get(key)
      if (!srcEntry) {
        return
      }
      if (srcEntry.order >= targetOrder) {
        violations.push({
          sourceId: srcRef,
          targetId: gatedQ?.id ?? gatedQ?.uniqueIdentifier ?? null,
          sourceOrder: srcEntry.order,
          targetOrder,
        })
      }
    })
  })
  return violations
}

const SCALE_RATING = {
  2: [2, 4],
  3: [2, 3, 4],
  4: [2, 3, 4, 5],
  5: [1, 2, 3, 4, 5],
}

const RATING_SENTIMENT = {
  1: 'negative',
  2: 'negative',
  3: 'neutral',
  4: 'positive',
  5: 'positive',
}

const SMALL_ICON_SET_MAP = {
  no_icon: null,
  smilie: images.smilie,
  smilie2: images.smilieAnimated.small,
  smilie2_static: images.smilieStatic.small,
  smilie_thumb: images.thumbAnimated.small,
  smilie_thumb_static: images.thumbStatic.small,
  smilie_cloud: images.cloudAnimated.small,
  smilie_cloud_static: images.cloudStatic.small,
  cat: images.catAnimated.small,
  cat_static: images.catStatic.small,
}

const BIG_ICON_SET_MAP = {
  no_icon: null,
  smilie: images.smilie,
  smilie2: images.smilieAnimated.big,
  smilie2_static: images.smilieStatic.big,
  smilie_thumb: images.thumbAnimated.big,
  smilie_thumb_static: images.thumbStatic.big,
  smilie_cloud: images.cloudAnimated.big,
  smilie_cloud_static: images.cloudStatic.big,
  cat: images.catAnimated.big,
  cat_static: images.catStatic.big,
}

const NPSScoreSentiment = (score) => {
  if (score >= 0 && score < 7) {
    return 'negative'
  } else if (score >= 7 && score < 9) {
    return 'neutral'
  } else if (score >= 9) {
    return 'positive'
  }
}

const CESScoreSentiment = (score) => {
  if (score >= 1 && score < 4) {
    return 'negative'
  } else if (score === 4) {
    return 'neutral'
  } else if (score >= 5 && score <= 7) {
    return 'positive'
  }
}

const starScoreSentiment = (score) => {
  if (score >= 1 && score < 3) {
    return 'negative'
  } else if (score === 3) {
    return 'neutral'
  } else if (score >= 4 && score <= 5) {
    return 'positive'
  }
}

function generateCreateCsatChoiceActions(formChoices, choicesResponse, question, questionIndex) {
  return formChoices
    .map((choiceLabel, index) => {
      const currentChoiceValueForIndex = String(SCALE_RATING[formChoices.length][index])
      const choiceByValue =
        choicesResponse.find(R.propEq('value', currentChoiceValueForIndex)) || {}
      const choiceExisted = Boolean(choiceByValue.id)
      return !choiceExisted
        ? {
            type: 'choice',
            action: 'create',
            data: {
              id: `${TEMP_ID_PRIMARY_PREFIX}${index + 1}`,
              value: currentChoiceValueForIndex,
              label: choiceLabel,
              order: index + 1,
              parent_question_order: questionIndex + 1,
              sentiment: RATING_SENTIMENT[currentChoiceValueForIndex],
            },
          }
        : null
    })
    .filter(R.identity)
}

function generateUpdateCsatChoiceActions(formChoices, choicesResponse, question, questionIndex) {
  return formChoices
    .map((choiceLabel, index) => {
      const currentChoiceValueForIndex = String(SCALE_RATING[formChoices.length][index])
      const choiceByValue =
        choicesResponse.find(R.propEq('value', currentChoiceValueForIndex)) || {}
      const choiceExisted = Boolean(choiceByValue.id)
      return choiceExisted
        ? {
            type: 'choice',
            action: 'update',
            data: {
              ...choiceByValue,
              value: currentChoiceValueForIndex,
              label: choiceLabel,
              order: index + 1,
              parent_question_order: questionIndex + 1,
              sentiment: RATING_SENTIMENT[currentChoiceValueForIndex],
            },
          }
        : null
    })
    .filter(R.identity)
}

function generateDeleteCsatChoiceActions(formChoices, choicesResponse) {
  return choicesResponse
    .map((choiceResponse, index) => {
      const currentChoiceValueForIndex = SCALE_RATING[choicesResponse.length][index]
      const formRatings = SCALE_RATING[formChoices.length]
      const choiceDeleted = !R.contains(currentChoiceValueForIndex, formRatings)
      const choiceExisted = Boolean(choiceResponse.id)
      return choiceDeleted && choiceExisted
        ? {
            type: 'choice',
            action: 'delete',
            data: {
              id: choiceResponse.id,
            },
          }
        : null
    })
    .filter(R.identity)
}

export function generateCsatChoicesRequest(choicesResponse, question, questionIndex) {
  const formChoices = question.choices.map((choice) => (choice.label ? choice.label : choice))

  const createActions = generateCreateCsatChoiceActions(
    formChoices,
    choicesResponse,
    question,
    questionIndex
  )

  const updateActions = generateUpdateCsatChoiceActions(
    formChoices,
    choicesResponse,
    question,
    questionIndex
  )

  const deleteActions = generateDeleteCsatChoiceActions(formChoices, choicesResponse)

  return createActions.concat(updateActions).concat(deleteActions)
}

export const generateSliderChoicesRequest = (question, questionIndex, action) => {
  const numberOfChoices = getNumberOfChoices(question, action)
  return Array(numberOfChoices)
    .fill()
    .map((_, choiceIndex) => {
      const value = R.pathOr(choiceIndex, ['choices', choiceIndex, 'value'], question)
      return {
        action: action,
        type: 'choice',
        data: {
          id: R.pathOr(null, ['choices', choiceIndex, 'id'], question),
          order: choiceIndex + 1,
          label: getSliderLabel(question, choiceIndex, numberOfChoices, value),
          value,
          sentiment: NPSScoreSentiment(value),
          parent_question_order: questionIndex + 1,
        },
      }
    })
}

function getSliderLabel(question, choiceIndex, numberOfChoices, value) {
  if (choiceIndex === 0) {
    return question.leftSideLabel
  } else if (choiceIndex === numberOfChoices - 1) {
    return question.rightSideLabel
  } else {
    return value
  }
}

export const generateStarChoicesRequest = (question, questionIndex, action) => {
  const numberOfChoices = getNumberOfChoices(question, action)
  return Array(numberOfChoices)
    .fill()
    .map((_, choiceIndex) => {
      const value = choiceIndex + 1
      return {
        action: action,
        type: 'choice',
        data: {
          id: R.pathOr(null, ['choices', choiceIndex, 'id'], question),
          order: choiceIndex + 1,
          label: getRatingScaleMinmaxLabel(question, choiceIndex, numberOfChoices),
          value,
          sentiment: starScoreSentiment(value),
          parent_question_order: questionIndex + 1,
        },
      }
    })
}

export const generateRatingScaleMinmaxChoicesRequest = (question, questionIndex, action) => {
  const numberOfChoices = getNumberOfChoices(question, action)
  return Array(numberOfChoices)
    .fill()
    .map((_, choiceIndex) => {
      const value = choiceIndex + 1
      return {
        action: action,
        type: 'choice',
        data: {
          id: R.pathOr(null, ['choices', choiceIndex, 'id'], question),
          order: choiceIndex + 1,
          label: getRatingScaleMinmaxLabel(question, choiceIndex, numberOfChoices),
          value,
          sentiment: CESScoreSentiment(value),
          parent_question_order: questionIndex + 1,
        },
      }
    })
}

function getRatingScaleMinmaxLabel(question, choiceIndex, numberOfChoices) {
  if (choiceIndex === 0) {
    return question.leftSideLabel
  } else if (choiceIndex === numberOfChoices - 1) {
    return question.rightSideLabel
  } else {
    return R.pathOr('', ['choices', choiceIndex, 'label'], question)
  }
}

function getNumberOfChoices(question, action) {
  if (action === 'create') {
    return question.numberOfChoices
  } else {
    return question.choices.length
  }
}

export const generateFollowUpQuestionsRequest = (primaryQuestion, questionIndex, action) => {
  const createFollowUpQuestions = []

  primaryQuestion.followUpQuestions.forEach((followupQuestion) => {
    createFollowUpQuestions.push({
      action: action,
      type: 'followUpQuestion',
      data: {
        ...followupQuestion,
        is_required: primaryQuestion.isFollowUpQuestionRequired,
        metric: constants.questionMetricType.custom,
        parent_question_order: questionIndex + 1,
      },
    })
  })
  return createFollowUpQuestions
}

export function sortChoiceValue(choices) {
  return R.sort(R.descend(R.compose(Number, R.prop('value'))), choices)
}

function _getActiveChoices(ratings, choices) {
  return choices.filter((choice) => {
    const choiceValue = typeof choice.value == 'string' ? Number(choice.value) : choice.value
    return ratings.includes(choiceValue)
  })
}

function _getChoices(question) {
  if (question.isOpinionScale) {
    let choices = question.choices
    // detect change rating scale and then save
    if (question.ratingScale) {
      const SCALE_RATING = {
        2: [4, 2],
        3: [4, 3, 2],
        4: [5, 4, 3, 2],
        5: [5, 4, 3, 2, 1],
      }
      const ratings = SCALE_RATING[question.ratingScale]
      choices = _getActiveChoices(ratings, question.choices)
    }
    return sortChoiceValue(choices)
  } else {
    return question.choices
  }
}

export const generateChoicesRequest = (question, questionIndex) => {
  const choices = _getChoices(question)
  const createChoices = choices.map((choice, index) => {
    const choiceIdExisted = R.prop('id', choice) && !`${choice.id}`.startsWith(TEMP_ID_PREFIX)
    const questionOrder = questionIndex + 1
    if (choiceIdExisted) {
      return {
        action: 'update',
        type: 'choice',
        data: {
          ...choice,
          order: index + 1,
          // BE: get question from  survey question order
          parent_question_order: questionOrder,
        },
      }
    } else {
      return {
        action: 'create',
        type: 'choice',
        data: {
          ...choice,
          order: index + 1,
          // BE: get question from  survey question order
          parent_question_order: questionOrder,
        },
      }
    }
  })

  const deletedChoicesState = R.propOr([], 'deletedExistChoices', question).filter(
    (choice) => !`${choice.id}`.startsWith(TEMP_ID_PREFIX)
  )
  const deletedExistChoices = deletedChoicesState.map((choice) => {
    return {
      action: 'delete',
      type: 'choice',
      data: {
        id: choice.id,
      },
    }
  })

  // Opinion-scale overflow choices (those beyond the rating scale) are kept in the
  // editor only — never persisted as active choices, since the respondent survey
  // and sentiment reporting render every active choice. Soft-delete the ones that
  // already exist in the DB so saving an opinion-scale question never leaves stray
  // non-rating options behind.
  const deletedIds = deletedChoicesState.map((choice) => `${choice.id}`)
  const preservedOverflowDeletes = question.isOpinionScale
    ? R.propOr([], 'preservedOpinionOverflowChoices', question)
        .filter(
          (choice) =>
            R.prop('id', choice) &&
            !`${choice.id}`.startsWith(TEMP_ID_PREFIX) &&
            !deletedIds.includes(`${choice.id}`)
        )
        .map((choice) => ({
          action: 'delete',
          type: 'choice',
          data: {
            id: choice.id,
          },
        }))
    : []

  return [...deletedExistChoices, ...preservedOverflowDeletes, ...createChoices]
}

export const generateQuestionRequest = (question, questionIndex, action) => {
  const formattedQuestion = {
    action: action,
    type: 'question',
    data: {
      id: R.prop('id', question) || R.prop('uniqueIdentifier', question),
      is_required: question.isRequired,
      order: questionIndex + 1,
      // PET-1965: metric comes from the question itself, not its position —
      // deriving it from `questionIndex === 0` overwrote the real metric
      // question with CUSTOM whenever a screener sat above it.
      metric: R.propOr(constants.questionMetricType.custom, 'metric', question),
      type: question.type,
      text: question.text,
      parent_question_order: null,
      display_follow_up_question: question.displayFollowUpQuestion,
      is_opinion_scale: !!R.prop('isOpinionScale', question),
      choice_icon_set: R.pathOr(null, ['choiceIconSet', 'id'], question),
    },
  }

  // hard code add default for primary question — identified by metric-ness,
  // not position, so a screener-first create still seeds the metric question's
  // conditional logic (PET-1965).
  if (action === 'create' && isMetricQuestion(question)) {
    formattedQuestion['data']['conditional_logic_type'] = question.conditionalLogicType
  }
  return formattedQuestion
}

export const generateNewQuestion = (type, questionIndex) => {
  const newQuestion = questionTypeJsonData[type]
  newQuestion['order'] = questionIndex
  return newQuestion
}

export const generateNewChoice = () => ({
  label: '',
})

const mapper = produce((question) => {
  // PET-1965: the CSAT metric question keeps its icon set wherever it sits —
  // additional questions are never metric CSAT, so no index guard is needed.
  if (question.metric === constants.questionMetricType.csat) {
    question.iconSetName = getImageName(question.choices[0].image)
  }

  if (
    [
      constants.questionType.slider,
      constants.questionType.rating_scale_minmax,
      constants.questionType.star,
    ].includes(question.type)
  ) {
    const lastIndex = question.choices.length - 1
    question.leftSideLabel = question.choices[0].label
    question.rightSideLabel = question.choices[lastIndex].label
  }
})

export const transformResponseToPresentationalState = (response) => {
  const presentationalState = humps.camelizeKeys(response.questions.map(mapper))
  presentationalState.forEach((question, index) => {
    question.choiceIconSet = response.questions[index].choice_icon_set
  })
  return presentationalState
}

const getImageName = (url) => {
  return (
    getImageNameInIconSetMap(url, SMALL_ICON_SET_MAP) ||
    getImageNameInIconSetMap(url, BIG_ICON_SET_MAP)
  )
}

function getImageNameInIconSetMap(url, iconSetMap) {
  for (const iconSetName in iconSetMap) {
    const iconSet = iconSetMap[iconSetName]
    if (R.isNil(iconSet)) {
      continue
    }

    for (const valueOrSentiment in iconSet) {
      const imageUrl = iconSet[valueOrSentiment]
      if (imageUrl === url) {
        return iconSetName
      }
    }
  }

  return null
}

export function getQuestionTemplate(metric, companyName) {
  const defaultTemplate = initialCSATPrimaryQuestion
  return (
    {
      [constants.questionMetricType.csat]: initialCSATPrimaryQuestion,
      [constants.questionMetricType.nps]: initialNPSPrimaryQuestion,
      [constants.questionMetricType.ces]: initialCESPrimaryQuestion(companyName),
      [constants.questionMetricType.star]: initialFiveStarPrimaryQuestion(companyName),
      [questionTemplateType.screener]: initialScreenerQuestion,
    }[metric] || defaultTemplate
  )
}

export function createDefaultChoicesResponse(formChoices) {
  return formChoices ? formChoices : constants.DEFAULT_CSAT_LABEL_STATE
}

export function hasSentimentRule(conditions) {
  const hasSentimentRule = conditions.some((condition) =>
    condition.rules.some((rule) => ['positive', 'neutral', 'negative'].includes(rule))
  )
  return hasSentimentRule
}

export const generateVisibilityRequest = (visibility, action) => {
  const nonChoiceIdRules = ['temp', 'positive', 'neutral', 'negative']
  const formattedVisibilityCondition = {
    action: action,
    type: 'visibilityCondition',
    data: {
      id: visibility.id,
      rule: visibility.rule.map((item) =>
        nonChoiceIdRules.some((nonIdChoice) => String(item).includes(nonIdChoice))
          ? item
          : parseInt(item, 10)
      ),
      conditional_question_id: visibility.conditionalQuestionId,
      source_question_id: visibility.sourceQuestionId,
    },
  }
  return formattedVisibilityCondition
}

export const generateDeleteVisibilityRequest = (visibility, action) => {
  const formattedVisibilityCondition = {
    action: action,
    type: 'visibilityCondition',
    data: {
      id: visibility.id,
    },
  }
  return formattedVisibilityCondition
}

export const generatePreviewLink = (surveyToken, surveyQuestions) => {
  if (surveyQuestions && !surveyQuestions?.questions?.[0]?.choices) {
    return ''
  }

  const url = `${constants.backendHost}/api/rating/${surveyToken}/?&customer_email=tester@example.com&preview=true`
  return url
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/SurveySettingsV2/utils.js